// v0.4.92 — Single horizontal strip of completed-step chips
// (operator sign-off 2026-06-20 = 2a).
//
// Pre-v0.4.92 each completed inactive step rendered a full-width
// `<StepCompletedSummary>` card in its slot in the Dashboard tree.
// With 5 steps complete + Step 6 active, the operator hit ~250px
// of "done" status before reaching the active workspace. Replaced
// by a single inline strip mounted under the Stepper. Each chip
// stays clickable to jump back. Summary text is identical so the
// information is preserved, just densified.
import React from "react";
import { useTranslation } from "react-i18next";
import { useWorkflow } from "../workflow/WorkflowContext";
import { stepLabelFor } from "../workflow/stepLabels";
import { offPeriodRows, reconciliationFailed } from "../lib/filingReadiness";
import { labelFor } from "../lib/enumLabels";
import { isNote } from "./review_queue/answers";

// A2-75 (2026-10-11): the strip summed review.section_totals, which counts
// every FLAGGED row whether or not it has been acknowledged, so it kept
// saying "2 unresolved reviews" through sign-off and freeze while the
// sign-off page said 0. Count what is still open: each group's rows less
// the ones acknowledged. Audit-only groups need no action.
export function unresolvedReviews(review) {
  const groups = review?.groups;
  if (Array.isArray(groups)) {
    return groups
      .filter((g) => !isNote(g))   // A1-103: notes are never unresolved
      // A2-640: rows of a country this calc files no return for are not open here
      .reduce((n, g) => n + Math.max((g.count || 0) - (g.acknowledged_count || 0) - offPeriodRows(g), 0), 0);
  }
  return (review?.section_totals
    && Object.values(review.section_totals).reduce((a, b) => a + b, 0)) || 0;
}

// Shared with Dashboard.jsx — derives the per-step one-line
// summary previously held inside `<StepCompletedSummary>`.
export const summarizeStep = (stepIdx, ctx) => {
  const { calc, selectedRunIds, runs, periodStart, periodEnd, review, clientId, clients } = ctx;
  const selectedRuns = (runs || []).filter((r) => (selectedRunIds || []).includes(r.id));
  if (stepIdx === 1 && selectedRuns.length) {
    const rowCount = selectedRuns.reduce((s, r) => s + (r.rows_accepted || 0), 0);
    const client = (clients || []).find((c) => c.id === clientId);
    // v0.5.7 Stage 5 — no bound client means the run used the built-in
    // default entity; show that, never system-speak "(no entity)".
    // Operator check 2026-10-16: only when NO client is selected — a
    // selected client whose name is not loaded yet is not the default.
    const isDefault = !clientId || clientId === "default";
    const clientName = client?.name || client?.code
      || (isDefault ? "Default client" : "Selected client");
    return `${clientName} · ${rowCount.toLocaleString()} rows`;
  }
  if (stepIdx === 2 && periodStart && periodEnd) {
    return `${periodStart} → ${periodEnd}, ${selectedRuns.length} run(s)`;
  }
  if (stepIdx === 3 && calc) {
    const ra = calc.regimes_applied || {};
    const regime = Object.entries(ra).sort((a, b) => b[1] - a[1])[0]?.[0] || "—";
    // A2-537 (Antigravity UX review): totals_by_country counted the seller's
    // country only, so a calc with GB and DE returns said "1 countries".
    // Count the countries the calculation produced a return row for.
    const local = new Set((calc.template_summary || [])
      .filter((r) => r["Row Type"] === "LOCAL").map((r) => r.Country));
    const countries = local.size || Object.keys(calc.totals_by_country || {}).length;
    return `${labelFor("regime", regime)} · ${countries} ${countries === 1 ? "country" : "countries"} · ${calc.transaction_count || 0} rows`;
  }
  if (stepIdx === 4) {
    return `${unresolvedReviews(review)} unresolved review${unresolvedReviews(review) === 1 ? "" : "s"}`;
  }
  if (stepIdx === 5) {
    const recon = calc?.reconciliation;
    if (reconciliationFailed(recon)) {
      const n = (recon.deltas || []).length;
      return `DO NOT FILE · ${n} calc-level issue${n === 1 ? "" : "s"}`;
    }
    // v0.5.0g+4 — scope the claim narrowly to what it ACTUALLY
    // checks (calc-level R1-R4: per-txn, aggregate, per-country-
    // bucket). Country-return form-level reconciliation is a
    // separate gate and lives on Step 6's country-returns row.
    // Without this scoping, the chip used to say
    // "RECONCILED · safe to file" while a country return was in
    // breach — same UI-lie class as v0.5.0g+1's Step 6 prereq
    // row.
    return "Calc R1-R4 reconciled";
  }
  if (stepIdx === 6 && calc?.frozen) {
    return `Frozen · ${calc.filing_reference || "no reference"}`;
  }
  return "click to re-edit";
};

const StepChip = ({ stepIdx, summary, danger, onClick }) => {
  const { t } = useTranslation();
  const label = stepLabelFor(stepIdx, t);
  return (
    <button
      type="button"
      data-testid={`completed-step-chip-${stepIdx}`}
      onClick={onClick}
      title={t("ui.completed_strip.chip_title", { n: stepIdx, label, defaultValue: `Step ${stepIdx} — ${label} — click to re-open` })}
      className={`group inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-[11px] transition-colors ${
        danger
          ? "border-rose-300 dark:border-rose-700 bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-200 hover:bg-rose-100 dark:hover:bg-rose-900/50"
          : "border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-200 hover:bg-emerald-100 dark:hover:bg-emerald-900/50"
      }`}
    >
      <span className={`font-bold ${danger ? "text-rose-600 dark:text-rose-300" : "text-emerald-600 dark:text-emerald-300"}`}>
        {danger ? "✗" : "✓"}
      </span>
      <span className="opacity-70">{stepIdx}.</span>
      <span className="font-semibold">{label}</span>
      <span className="opacity-70 truncate max-w-[280px]">· {summary}</span>
    </button>
  );
};

export const CompletedStepsStrip = () => {
  const { t } = useTranslation();
  const ctx = useWorkflow();
  const { state, setActiveStep, activeStep, calc } = ctx;
  const completed = [1, 2, 3, 4, 5, 6].filter(
    (i) => state?.steps?.[i]?.complete && i !== activeStep,
  );
  if (completed.length === 0) return null;
  return (
    <div
      data-testid="completed-steps-strip"
      className="border-y border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/40 -mx-6 lg:-mx-8 px-6 lg:px-8 py-2"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400 mr-1">
          {t("ui.completed_strip.label", { defaultValue: "Completed" })}
        </span>
        {completed.map((idx, i) => {
          const summary = summarizeStep(idx, ctx);
          const danger = idx === 5 && reconciliationFailed(calc?.reconciliation);
          return (
            <React.Fragment key={idx}>
              {i > 0 && (
                <span aria-hidden className="text-slate-400 dark:text-slate-600 text-[11px]">›</span>
              )}
              <StepChip
                stepIdx={idx}
                summary={summary}
                danger={danger}
                onClick={() => setActiveStep(idx)}
              />
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default CompletedStepsStrip;
